import { useState, useEffect, useRef } from "react";
import { Widget } from "@typeform/embed-react";
import { useTranslation } from "next-i18next";
import { UTSLocale } from "@smile2impress/attraction-constants";
import { useExperimentContext } from "context/experimentContext";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import { SentryErrorType } from "constants/enum";
import { EXPERIMENT_NAMES, EXPERIMENTS } from "constants/experiments";
import { reportToSentry } from "utils/reportToSentry";
import { GTMEvents } from "services/AnaliticsService/events";
import { sendGTMEvent } from "services/AnaliticsService/sendGoogleAnalitic";

import { TypeformIdsA, TypeformIdsB } from "./constants";
import { TypeformProps, WithFormId, WithResponseId } from "./Typeform.types";

function getTypeFormIds(groups: { [key: string]: number }) {
    const { controlGroupId, experimentalGroupId } =
        EXPERIMENTS[EXPERIMENT_NAMES.TypeForm];
    switch (groups[EXPERIMENT_NAMES.TypeForm]) {
        case controlGroupId:
            return TypeformIdsA;
        case experimentalGroupId:
            return TypeformIdsB;
        default:
            return null;
    }
}

export const Typeform = ({ typeformIds, onSubmit }: TypeformProps) => {
    const { locale } = useUTSLocaleContext();
    const { t } = useTranslation("global");

    const [typeformId, setId] = useState<null | string>(null);
    const [localGroups, setGroups] = useState<Record<string, number>>({});
    const { groups } = useExperimentContext();
    const hasFiredViewedRef = useRef(false);

    useEffect(() => {
        setGroups(groups);
    }, [groups]);

    const getIdFromGroups = (grs: Record<string, number>, loc: UTSLocale) => {
        // If typeformIds prop is provided, use it directly
        if (typeformIds) {
            return typeformIds[loc] || "";
        }

        // Otherwise, use the experiment-based logic
        const ids = getTypeFormIds(grs);
        return ids?.[loc] || "";
    };

    useEffect(() => {
        // If typeformIds prop is provided, use it directly without experiment logic
        if (typeformIds) {
            const newId = typeformIds[locale] || "";
            setId(newId);
            if (!newId) {
                reportToSentry({
                    error: SentryErrorType.NO_TYPEFORM_ID,
                    extra: { typeformId: newId },
                    level: "info",
                });
            }
            return;
        }

        // Original experiment-based logic
        if (groups.typeform) {
            setId((prev) => {
                const newId = getIdFromGroups(groups, locale);
                const next = newId || prev;
                if (!next) {
                    reportToSentry({
                        error: SentryErrorType.NO_TYPEFORM_ID,
                        extra: { typeformId },
                        level: "info",
                    });
                }
                return next as string;
            });
        }
    }, [localGroups?.typeform, typeformIds, locale]);

    useEffect(() => {
        hasFiredViewedRef.current = false;
    }, [typeformId]);

    const handleReady = () => {
        if (hasFiredViewedRef.current) {
            return;
        }

        hasFiredViewedRef.current = true;
        sendGTMEvent(GTMEvents.TYPEFORM_VIEWED_GTM_EVENT);
    };

    const handleSubmit = (event: WithFormId & WithResponseId) => {
        if (onSubmit) {
            onSubmit(event);
        }
    };

    return (
        <>
            {typeformId && (
                <Widget
                    id={typeformId}
                    style={{ width: "100%", height: "100%" }}
                    iframeProps={{ title: t("headerQuizButtonText") }}
                    transitiveSearchParams
                    medium="snippet"
                    onReady={handleReady}
                    onSubmit={handleSubmit}
                    inlineOnMobile={true}
                    hidden={{
                        utm_source: "",
                        utm_medium: "",
                        utm_term: "",
                        utm_content: "",
                        hubspot_utk: "",
                        hubspot_page_name: "",
                        hubspot_page_url: "",
                    }}
                />
            )}
        </>
    );
};
